import type { TransportResult } from '@krp/client-runtime'

import type { WorkplaceToken } from './agentSessionController'

/**
 * Токен рабочего места для кассового агента: его подписывает Platform (`POST /api/platform/v1/me/agent-token`, KA3) вошедшему
 * человеку, на 10 минут и для одного запущенного агента (`instanceId` из его `hello`). Здесь только разбор ответа и превращение
 * отказа в русский текст; сам запрос идёт через общий transport (cookie, CSRF), а токен живёт в памяти контроллера и нигде не пишется.
 */

/** Запрос токена для агента с этим `instanceId` — через transport приложения. */
export type AgentTokenTransport = (agentInstanceId: string) => Promise<TransportResult<unknown>>

/** Ответ платформы: `token` и срок `expiresAt` (ISO). Без токена или без понятного срока ответ не годится: срок нужен, чтобы вовремя обновить. */
export function decodeAgentToken(raw: unknown): WorkplaceToken {
  if (typeof raw !== 'object' || raw === null) throw new Error('Платформа вернула токен рабочего места в неожиданном виде.')
  const value = raw as { token?: unknown; expiresAt?: unknown }
  const token = typeof value.token === 'string' && value.token.split('.').length === 3 ? value.token : null
  const expiresAt = typeof value.expiresAt === 'string' ? Date.parse(value.expiresAt) : Number.NaN
  if (token === null || Number.isNaN(expiresAt)) throw new Error('Платформа вернула токен рабочего места в неожиданном виде.')
  return { token, expiresAt }
}

/**
 * Получить токен. Отказ платформы показывается её словами (ADR-8): нет входа, выдача не настроена (503 `agent-tokens-disabled`),
 * нет связи. Исключение — всегда `Error` с русским текстом: контроллер закрывает продажу и показывает его кассиру.
 */
export function createTokenFetcher(transport: AgentTokenTransport): (agentInstanceId: string) => Promise<WorkplaceToken> {
  return async (agentInstanceId) => {
    const result = await transport(agentInstanceId)
    if (!result.ok) throw new Error(result.error.message === '' ? 'Не удалось получить токен рабочего места.' : result.error.message)
    return decodeAgentToken(result.data)
  }
}
